import { Grid, GridColumn, ISuggestionItem, SuggestionField } from 'telia-front-react';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { debounce } from 'throttle-debounce';

import AddressStore from '../../../../stores/AddressStore';
import { Address } from '../../../../stores/models/Address';
import TranslateStore from '../../../../stores/TranslateStore';

import { mapAddressesToAutocompleteItems, sortAndMapRoomsToSuggestionItems } from './AddressAutocompleteUtil';

export interface IAddressAutocompleteProps {
  onSubmit: (address: Address, room?: string, hasError?: boolean) => void;
  addressHasError?: boolean;
  apartmentHasError?: boolean;
  label: string;
  initialAddress?: Address;
  addressStore?: AddressStore;
  translateStore?: TranslateStore;
}

@inject('addressStore', 'translateStore')
@observer
export default class AddressAutocomplete extends React.Component<IAddressAutocompleteProps> {
  @observable
  private selectedAddress: Address = null;
  @observable
  private addressText = '';
  @observable
  private roomText = '';
  @observable
  private addresses: Address[] = [];
  @observable
  private allRoomOptions: ISuggestionItem[] = [];
  @observable
  private roomSuggestionItems: ISuggestionItem[] = [];
  @observable
  private roomField: HTMLInputElement | HTMLTextAreaElement = null;

  private debouncedFetchAddressAutocompleteOptions: () => void;

  constructor(props: IAddressAutocompleteProps) {
    super(props);
    makeObservable(this);
  }

  public async componentDidMount(): Promise<void> {
    await this.componentDidMountAction();
  }

  @action
  private async componentDidMountAction() {
    if (this.props.initialAddress) {
      this.addressText = this.props.initialAddress.fullAddressWithoutRoomName;
      this.roomText = this.props.initialAddress.roomName;
      this.selectedAddress = this.props.initialAddress;

      if (!this.selectedAddress.rooms) {
        try {
          const addresses = await this.props.addressStore.fetchAddressResult(this.addressText);
          this.selectedAddress.rooms = addresses.find((a) => a.buildingId === this.selectedAddress.buildingId)?.rooms;
        } catch (e) {
          console.error(e);
        }
      }
      this.allRoomOptions = sortAndMapRoomsToSuggestionItems(this.selectedAddress.rooms);
      this.roomSuggestionItems = this.allRoomOptions;
    }

    this.debouncedFetchAddressAutocompleteOptions = debounce(350, this.fetchAddressSuggestions);
  }

  @action
  private onRoomSuggestionSelection = (suggestion: ISuggestionItem) => {
    this.roomText = suggestion.label;
    this.submitChange();
  };

  @action
  private setAllRoomOptionsAndRoomSuggestionItems = (rooms: ISuggestionItem[]) => {
    this.allRoomOptions = rooms;
    this.roomSuggestionItems = this.allRoomOptions;
  };

  @action
  private onAddressAutocompleteSelection = (suggestion: ISuggestionItem) => {
    this.addressText = suggestion.label;
    this.selectedAddress = this.addresses.find((val) => val.registryId.toString() === suggestion.id);
    if (this.selectedAddress.rooms?.length) {
      this.setAllRoomOptionsAndRoomSuggestionItems(sortAndMapRoomsToSuggestionItems(this.selectedAddress.rooms));
      this.roomField && this.roomField.focus();
    } else {
      this.setAllRoomOptionsAndRoomSuggestionItems([]);
    }
    this.submitChange();
  };

  @action
  private setNewSearchTerm(searchTerm: string) {
    this.addressText = searchTerm;
    this.selectedAddress = null;
  }

  @action
  private onAddressAutocompleteChange(searchTerm: string) {
    this.selectedAddress = null;
    this.addressText = searchTerm;
    this.roomText = '';
    this.setAllRoomOptionsAndRoomSuggestionItems([]);
    this.submitChange();
    this.debouncedFetchAddressAutocompleteOptions();
  }

  @action
  private onRoomAutocompleteChange(roomValue: string) {
    this.roomText = roomValue;

    if (!roomValue) {
      this.roomSuggestionItems = this.allRoomOptions;
    } else {
      const roomValueLowerCase = roomValue.toLocaleLowerCase();
      this.roomSuggestionItems = this.allRoomOptions.filter((room) => {
        const suggestedRoomLowerCase = room.label.toLowerCase();
        return suggestedRoomLowerCase.startsWith(roomValueLowerCase, 0) && suggestedRoomLowerCase.includes(roomValueLowerCase);
      });
    }

    this.submitChange();
  }

  private fetchAddressSuggestions() {
    this.props.addressStore
      .fetchAddressResult(this.addressText)
      .then((addresses) => this.onFetchAddressSuccess(addresses))
      .catch(console.error);
  }

  @computed
  private get addressSuggestions() {
    return mapAddressesToAutocompleteItems(this.addresses, this.addressText);
  }

  @action
  private onFetchAddressSuccess(addresses: Address[]) {
    this.addresses = addresses;
  }

  private submitChange() {
    this.props.onSubmit(this.selectedAddress, this.roomText);
  }

  public render(): JSX.Element {
    const {
      translateStore: { translate },
      addressHasError,
      apartmentHasError
    } = this.props;
    return (
      <Grid>
        <GridColumn width={['sm-8']}>
          <SuggestionField
            id="address-field"
            icon="#search"
            value={this.addressText || ''}
            label={this.props.label}
            items={this.addressSuggestions}
            onChange={(searchTerm) => {
              if (searchTerm !== this.addressText) {
                this.onAddressAutocompleteChange(searchTerm);
              }
            }}
            onSelect={this.onAddressAutocompleteSelection}
            helper={
              addressHasError && {
                text: translate('invoices.invoice-settings-modal.errors.missing-address'),
                type: 'error'
              }
            }
          />
        </GridColumn>
        <GridColumn width={['sm-4']}>
          <SuggestionField
            id="apartment-field"
            value={this.roomText || ''}
            label={translate('invoices.invoice-settings-modal.appartment-number')}
            items={this.roomSuggestionItems}
            onChange={(roomValue) => this.onRoomAutocompleteChange(roomValue)}
            onSelect={this.onRoomSuggestionSelection}
            showSuggestionsOnFocus={true}
            helper={
              apartmentHasError && {
                text: translate('invoices.invoice-settings-modal.errors.missing-faulty-apartment-number'),
                type: 'error'
              }
            }
            inputRef={(element: HTMLInputElement | HTMLTextAreaElement) => {
              if (element && !this.roomField) {
                this.roomField = element;
              }
            }}
          />
        </GridColumn>
      </Grid>
    );
  }
}
